import {isUndefined} from 'lodash';
import React, {FC, useCallback, useRef} from 'react';
import styled from 'styled-components';

import {isDefined} from '../../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {TeammateModel} from '../../../../../../libs/shared/core/src/models/api/teammateModel';
import {ItemIndexes} from '../../../../../../srcCommon/helpers/itemIndexHelpers';
import {CellsConversationSearchResultsContent} from '../../../../../../srcCommon/inboxes/search/cellsSearchResultsSelectors';
import {CellsConversationsDragItem} from '../../../../../helpers/conversationCellHelpers';
import {ConversationSearchResultModel} from '../../../../../models/api/conversationSearchResultModel';
import {CellsPanelConversationsPageStateWithSearchResults} from '../../../../../store/panels/cells/cellsPanelState';
import {CellsPanelSearchAppliedQuerySuggestion} from '../../../../../store/panels/cells/search/cellsPanelSearchState';
import {
  InboxesConversationsParameters,
  InboxesParametersActivity,
} from '../../../../../store/panels/inboxes/parameters/inboxesParameters';
import {
  computeIsIdSelected,
  computeIsMultipleIdsSelected,
  findOnlySelectedId,
  InboxesSelection,
  normalInboxesSelection,
  setInboxesParametersSelection,
} from '../../../../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {ReadStatusInfoBubble} from '../../../../infoBubbles/readStatusInfoBubble';
import {useCompactP2Context} from '../../compactP2Context';
import {ConversationCellClickEventHandler} from '../../conversationCell';
import {ConversationSearchResultCellContainer} from '../../conversationSearchResultCellContainer';
import {CellsConversationsChildrenProps} from '../cellsConversations';
import {CellsItems} from '../cellsItemsBase';
import {CellsSearchAppliedQuerySuggestion} from '../cellsSearchAppliedQuerySuggestion';

/*
 * Props.
 */

interface CellsConversationsSearchResultsProps extends CellsConversationsChildrenProps {
  user: TeammateModel;
  page: CellsPanelConversationsPageStateWithSearchResults;
  content: CellsConversationSearchResultsContent;
  selectedDragItem: CellsConversationsDragItem;
  appliedQuerySuggestion: CellsPanelSearchAppliedQuerySuggestion | undefined;
  clearAppliedQuerySuggestion: () => void;
  queryHash: string | undefined;
}

// The following grid-row: 1 refers to the grid defined in src/components/inboxes/cells/cells.tsx.
const StyledCellsSearchAppliedQuerySuggestion = styled(CellsSearchAppliedQuerySuggestion)<{
  $isCompact: boolean;
}>`
  grid-row: 1;
  margin-top: 16px;
  margin-right: ${(p) => (p.$isCompact ? 7 : 8)}px;
  margin-left: 14px;
  margin-bottom: 8px;
`;

/*
 * Component.
 */

export const CellsConversationsSearchResults: FC<CellsConversationsSearchResultsProps> = (props) => {
  const {
    page,
    content,
    buildParametersPath,
    onParametersChange,
    onDoubleClick,
    user,
    appliedQuerySuggestion,
    clearAppliedQuerySuggestion,
    queryHash,
  } = props;
  const {parameters} = page;
  const {conversationSearchResults, conversationIndexes, conversationIds} = content;

  /*
   * Callbacks.
   */

  const buildSelectionParameters = useCallback(
    (selection: InboxesSelection<number>): InboxesConversationsParameters =>
      setInboxesParametersSelection(parameters, {
        selection,
        activity: findSearchActivityForConversationSelection(
          conversationSearchResults,
          conversationIndexes,
          selection,
        ),
      }),
    [parameters, conversationSearchResults, conversationIndexes],
  );

  const buildConversationPath = useCallback(
    (conversationId: number): string | undefined =>
      buildParametersPath
        ? buildParametersPath(buildSelectionParameters(normalInboxesSelection([conversationId])))
        : undefined,
    [buildSelectionParameters, buildParametersPath],
  );

  const onSelectionChange: (arg: InboxesSelection<number>) => void = useCallback(
    (selection) => onParametersChange(buildSelectionParameters(selection)),
    [buildSelectionParameters, onParametersChange],
  );

  /*
   * Refs.
   */

  const firstCellRef = useRef<HTMLDivElement>(null);

  /*
   * Scroller.
   */

  const {cellsContext} = page;
  const {selection} = parameters;

  const focusedId = findOnlySelectedId(selection);
  const areHoverActionsEnabled = !computeIsMultipleIdsSelected(selection);

  const renderItem = useCallback(
    (id: number, onClick: ConversationCellClickEventHandler) => {
      const index = conversationIndexes[id];
      const conversationSearchResult = conversationSearchResults[index];
      const isSelected = computeIsIdSelected(selection, conversationSearchResult.id);
      const maybeInnerRef = (index === 0 && firstCellRef) || undefined;

      return (
        <ConversationSearchResultCellContainer
          searchResult={conversationSearchResult}
          conversationHighlight={conversationSearchResult.mergedHighlights}
          cellsContext={cellsContext}
          parameters={parameters}
          isSelected={isSelected}
          customDragItem={isSelected ? props.selectedDragItem : undefined}
          path={buildConversationPath(id)}
          onClick={onClick}
          onDoubleClick={onDoubleClick}
          selection={selection}
          innerRef={maybeInnerRef}
          areHoverActionsEnabled={areHoverActionsEnabled}
          isFirst={index === 0}
          isLast={index === conversationSearchResults.length - 1}
        />
      );
    },
    [
      conversationIndexes,
      conversationSearchResults,
      selection,
      cellsContext,
      parameters,
      buildConversationPath,
      onDoubleClick,
      areHoverActionsEnabled,
      props.selectedDragItem,
    ],
  );

  /*
   * Render.
   */

  const {isCompact} = useCompactP2Context();

  return (
    <CellsItems
      userId={user.id}
      panelId={props.panelId}
      hasNextPage={props.hasNextPage}
      page={page}
      reselectDirection={props.reselectDirection}
      itemIds={conversationIds}
      itemIndexes={conversationIndexes}
      renderItem={renderItem}
      defaultId={0}
      focusedId={focusedId}
      onSelectionChange={onSelectionChange}
      onRetryClick={props.onRetryClick}
      onRequestMoreAsync={props.onRequestMoreAsync}
      isCompact={isCompact}
      isOffline={props.isOffline}
      shouldNotIncludeTopPadding={isDefined(appliedQuerySuggestion)}
    >
      {(children, options) => (
        <>
          {props.children(
            <>
              {appliedQuerySuggestion && (
                <StyledCellsSearchAppliedQuerySuggestion
                  $isCompact={isCompact}
                  originalQuery={appliedQuerySuggestion.originalQuery}
                  originalQueryNormalTerms={appliedQuerySuggestion.originalQueryNormalTerms}
                  suggestedQueryNormalTerms={appliedQuerySuggestion.suggestedQueryNormalTerms}
                  onAppliedQuerySuggestionClick={clearAppliedQuerySuggestion}
                  queryHash={queryHash}
                />
              )}
              {children}
            </>,
            options,
          )}
          {firstCellRef.current && <ReadStatusInfoBubble anchor={firstCellRef} />}
        </>
      )}
    </CellsItems>
  );
};

/*
 * Helpers.
 */

function findSearchActivityForConversationSelection(
  searchResults: readonly ConversationSearchResultModel[],
  conversationIndexes: ItemIndexes,
  selection: InboxesSelection<number>,
): InboxesParametersActivity | undefined {
  // If we have more than one selected ID, nothing to do.
  const conversationId = findOnlySelectedId(selection);
  if (isUndefined(conversationId)) {
    return undefined;
  }

  // Otherwise, check if we have an activity ID to focus from the search highlight.
  const searchResultId = conversationIndexes[conversationId];
  const highlight = searchResults[searchResultId]?.mergedHighlights;
  const searchActivityId = highlight?.searchActivity?.id;
  if (isUndefined(searchActivityId)) {
    return undefined;
  }

  return {id: searchActivityId};
}
